import React, { useState, useEffect } from 'react'
import { FiDownload } from 'react-icons/fi'
import { Lock, LockOpen, Visibility } from '@mui/icons-material'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import PersonIcon from '@mui/icons-material/Person'
import Skeleton from '@mui/material/Skeleton'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import Notfound from '../../../Notfound'
import { TableShimmer } from '../../../ShimmerUI'
import ClearIcon from '@mui/icons-material/Clear'
import api from '@/services/api'
import dayjs from 'dayjs'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CardContent,
  Card,
  Box,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material'
import { toast } from 'react-toastify'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
interface Insurance {
  insuranceName: string
  insuranceType: string
  payerId: string
  policyNumber: string
  groupNumber: string
  policyHolder: string
  copay: number
  expiryDate: string
  insuranceCardUrl: string | null
}

interface Patient {
  id: number
  firstName: string
  lastName: string
  email: string
  phone: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  dob?: string // or Date type, depending on how you handle date formatting
  gender?: string
  onboardingdate?: string // or Date type
  isActive: boolean
  insuranceProvider?: string
  createdAt?: string
  insurance?: Insurance[]
  _id: string // Assuming this is unique and used to identify the patient
}

const PatientManagementPage: React.FC = () => {
  const perPage = 10
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [pageSize, setPageSize] = useState(10)
  const [services, setServices] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [loadingForShimmer, setLoadingForShimmer] = useState(true)
  const [openToggle, setOpenToggle] = useState(false) // Dialog open state
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null
  )

  const [open, setOpen] = useState(false)
  const [selectedJobs, setSelectedJobs] = useState<any[]>([])
  const [searchName, setSearchName] = useState('')
  const [accountStatusFilter, setAccountStatusFilter] = useState('All')
  const [isActive, setIsActive] = useState<boolean>(false)
  const [patients, setPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false) // Loading state for export
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [error, setError] = useState('')
  const [selectedPlanFilter, setSelectedPlanFilter] = useState('')
  const [selectedAccountStatus, setSelectedAccountStatus] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalActiveRecords, setTotalActiveRecords] = useState(0)
  const [totalInactiveRecords, setTotalInactiveRecords] = useState(0)
  const [sortField, setSortField] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleOpen = (jobs: any[]) => {
    setSelectedJobs(jobs)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedJobs([])
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const params: Record<string, any> = {
        accountStatus:
          selectedAccountStatus === 'Active'
            ? true
            : selectedAccountStatus === 'Inactive'
              ? false
              : undefined,
      }

      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }

      if (startDate) {
        params.startDate = getFormattedDateForAPI(startDate)
      }

      if (endDate) {
        params.endDate = getFormattedDateForAPI(endDate)
      }

      const response = await api.get('admin/patients/export', {
        responseType: 'blob',
        params,
      })

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'text/csv',
      })

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Get filename from Content-Disposition header or use default
      // const contentDisposition = response.headers['content-disposition']
      let filename = 'patients_export.csv'
      // if (contentDisposition) {
      //   const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
      //   if (filenameMatch) {
      //     filename = filenameMatch[1]
      //   }
      // }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Export completed successfully')
    } catch (error) {
      console.error('Error exporting patients:', error)
      toast.error('Failed to export patients data')
    } finally {
      setExportLoading(false)
    }
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
    fetchPatients(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }

  const handleAccountStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccountStatus(event.target.value)
  }
  const getFormattedDateForAPI = (date: Date | null): string | null => {
    if (!date) return null

    const year = date.getFullYear()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)

    return `${year}-${month}-${day}` // YYYY-MM-DD format
  }
  const fetchPatients = async (page?: number) => {
    setLoading(true)
    setError('')
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        page: page || 1, // Default to 1 if page is not provided
        limit: 10,
        accountStatus:
          selectedAccountStatus === 'Active'
            ? true
            : selectedAccountStatus === 'Inactive'
              ? false
              : undefined,
      }

      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }
      if (startDate) {
        params.startDate = getFormattedDateForAPI(startDate)
      }

      if (endDate) {
        params.endDate = getFormattedDateForAPI(endDate)
      }

      // Add sorting parameters
      if (sortField) {
        params.sortField = sortField
        params.sortOrder = sortOrder
      }

      const response = await api.get('admin/patients', {
        params,
      })

      if (response?.data?.data) {
        setPatients(response.data.data)
      } else {
        setError('No patients data found')
      }

      setTotalRecords(response?.data?.stats?.totalPatients)
      setTotalActiveRecords(response?.data?.stats?.totalActivePatients)
      setTotalInactiveRecords(response?.data?.stats?.totalInactivePatients)
      setTotalPages(Math.ceil(response?.data?.totalRecords / pageSize))
    } catch (err: any) {
      console.error(err)
      setError('Failed to fetch patients')
    } finally {
      setLoadingForShimmer(false)
      setLoading(false)
    }
  }

  const handleReset = () => {
    setSearchKeyword('')
    setSelectedAccountStatus('')
    setStartDate(null)
    setEndDate(null)
    setSortField('')
    setSortOrder('asc')
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon className="w-4 h-4 ml-1" />
    }
    return sortOrder === 'asc' ? (
      <KeyboardArrowUpIcon className="w-4 h-4 ml-1" />
    ) : (
      <KeyboardArrowDownIcon className="w-4 h-4 ml-1" />
    )
  }

  useEffect(() => {
    fetchPatients(1)
  }, [
    searchKeyword,
    selectedAccountStatus,
    startDate,
    endDate,
    sortField,
    sortOrder,
  ])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const dd = String(date.getDate()).padStart(2, '0') // Day
    const mm = String(date.getMonth() + 1).padStart(2, '0') // Month
    const yyyy = date.getFullYear() // Full year
    return `${dd}-${mm}-${yyyy}` // Return formatted date as DD-MM-YYYY
  }

  const handleOpenToggle = (patientId: string, currentStatus: boolean) => {
    setSelectedPatientId(patientId) // Set the selected patient to toggle
    setIsActive(currentStatus) // Set the current status of the patient (active or inactive)
    setOpenToggle(true) // Open the confirmation dialog
  }
  const handleConfirmToggle = async () => {
    if (!selectedPatientId) return // Exit if no patient is selected
    setLoading(true) // Start loading

    const newStatus = !isActive // Toggle the status
    try {
      const response = await api.patch(
        `admin/patient/${selectedPatientId}/status`,
        {
          isActive: newStatus,
        }
      )

      if (response.status === 200) {
        setIsActive(newStatus) // Toggle status locally
        toast.success(
          `Patient status updated to ${newStatus ? 'Active' : 'Inactive'}`
        )
      }

      setOpenToggle(false) // Close the dialog after success
      fetchPatients() // Optionally refresh the patient list
    } catch (error) {
      console.error('Error updating the patient status:', error)
      toast.error('Failed to update patient status')
    } finally {
      setLoading(false) // Stop loading
    }
  }
  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
        <h5 className="text-2xl font-semibold text-[#6E7B8B]">
          Patient Management
        </h5>
        <button
          className="flex items-center gap-2 px-4 py-2 text-white bg-[#5BC0BE] border border-gray-300 rounded-lg hover:bg-[#49a9a7] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleExport}
          disabled={exportLoading}
          style={{
            cursor: exportLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <FiDownload className="w-4 h-4" />
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          boxShadow: 2,
          padding: 2,
          mb: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Search Patient
              </label>
              <input
                type="text"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="Search Patient"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Account Status
              </label>
              <select
                id="accountStatus"
                value={selectedAccountStatus}
                onChange={handleAccountStatusChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              >
                <option value="" disabled selected>
                  Select Status
                </option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" alignItems="flex-start" gap={2} width="100%">
                <Box>
                  <Typography variant="body2" mb={0.5}>
                    Start Date
                  </Typography>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue: Date | null) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: false,
                        sx: { width: '150px' },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" mb={0.5}>
                    End Date
                  </Typography>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue: Date | null) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: false,
                        sx: { width: '150px' },
                      },
                    }}
                  />
                </Box>

                {/* Reset All Button */}
                <Box display="flex" alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReset}
                    sx={{
                      borderRadius: 2,
                      width: '113px',
                      marginTop: '24px',
                    }}
                  >
                    Reset All
                  </Button>
                </Box>
              </Box>
            </LocalizationProvider>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow h-35">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{totalRecords}</h2>
            <p className="text-sm text-gray-500">Total Patients</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-green-600 bg-green-100 rounded-full">
            <PersonIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {totalActiveRecords}
            </h2>
            <p className="text-sm text-gray-500">Active Patient</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-blue-600 bg-blue-100 rounded-full">
            <CheckIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {totalInactiveRecords}
            </h2>
            <p className="text-sm text-gray-500">Inactive Patient</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-red-600 bg-red-100 rounded-full">
            <ClearIcon />
          </div>
        </div>
      </div>

      {/* Patient Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-white uppercase bg-[#5BC0BE]">
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center">
                    Name
                    {getSortIcon('firstName')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    Email
                    {getSortIcon('email')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center">
                    Phone
                    {getSortIcon('phone')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('addressLine1')}
                >
                  <div className="flex items-center">
                    Address
                    {getSortIcon('addressLine1')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('dob')}
                >
                  <div className="flex items-center">
                    DOB
                    {getSortIcon('dob')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('gender')}
                >
                  <div className="flex items-center">
                    Gender
                    {getSortIcon('gender')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 cursor-pointer"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center">
                    Onboarding Date
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-6 py-3">Insurance</th>
                <th className="px-6 py-3">Account Status</th>
                {/* <th className="px-6 py-3">Posted Jobs</th> */}
                <th className="px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingForShimmer ? (
                <TableShimmer columns={10} />
              ) : patients?.length === 0 ? (
                <tr>
                  <td colSpan={20} className="p-4 text-center">
                    <Notfound />
                  </td>
                </tr>
              ) : (
                patients?.map(patient => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 min-w-[200px]">
                      {patient?.firstName} {patient?.lastName}
                    </td>
                    <td className="px-6 py-4">{patient?.email}</td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {patient?.phone}
                    </td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {`${patient?.addressLine1 || ''} ${patient?.addressLine2 || ''} ${patient?.city || ''} ${patient?.state || ''} ${patient?.country || ''} ${patient?.postalCode || ''}`.trim() ||
                        '--'}
                    </td>

                    <td className="px-6 py-4 min-w-[200px]">
                      {patient?.dob
                        ? dayjs(patient.dob).isValid()
                          ? dayjs(patient.dob).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      {patient?.gender
                        ? patient?.gender.charAt(0).toUpperCase() +
                          patient?.gender.slice(1).toLowerCase()
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      {patient?.createdAt
                        ? dayjs(patient.createdAt).isValid()
                          ? dayjs(patient.createdAt).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {patient?.insurance?.length
                        ? patient.insurance.map((ins, index) => (
                            <div key={index}>{ins.insuranceName}</div>
                          ))
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          patient?.isActive
                            ? 'border-green-500 text-green-600'
                            : 'border-red-500 text-red-600'
                        }`}
                      >
                        {patient?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <IconButton
                        onClick={() =>
                          handleOpenToggle(patient?._id, patient?.isActive)
                        }
                      >
                        <Tooltip
                          title={patient?.isActive ? 'Deactivate' : 'Activate'}
                          arrow
                        >
                          {patient?.isActive ? (
                            <ToggleOnIcon
                              sx={{ fontSize: 32, color: '#10b981' }}
                            />
                          ) : (
                            <ToggleOffIcon
                              sx={{ fontSize: 32, color: '#ef4444' }}
                            />
                          )}
                        </Tooltip>
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
          <RowsPerPageDropdown
            pageSize={pageSize}
            onPageSizeChange={newPageSize => {
              handlePageSizeChange(newPageSize)
              if (ourPageSize && ourPageSize !== newPageSize) {
                setCurrentPage(1)
              }
            }}
          />
        </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-1 mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>

      {/* Jobs Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Posted Jobs</DialogTitle>
        <DialogContent dividers>
          {selectedJobs.length > 0 ? (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Status</TableCell>
                    {/* <TableCell>Provider Name</TableCell> */}
                    <TableCell>Posted Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedJobs.map(job => (
                    <TableRow key={job.jobId}>
                      <TableCell>{job.jobId}</TableCell>
                      <TableCell>{job.jobType}</TableCell>
                      <TableCell>{job.status}</TableCell>
                      {/* <TableCell>{job.assignedTherapist || 'N/A'}</TableCell> */}
                      <TableCell>{formatDate(job.postedDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography>No jobs found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Active/incative */}
      <Dialog open={openToggle} onClose={() => setOpenToggle(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to {isActive ? 'deactivate' : 'activate'} this
          patient?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenToggle(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </div>
  )
}

export default PatientManagementPage
